<script setup>
import {computed, ref} from "vue";
import {getNoun} from "@/helpers/string";
import useGrid from "@/composables/useGrid";
import TablePager from "@/components/TablePager.vue";
import inviteService from "@/services/invite.service";
import {download} from "@/helpers/file";

const {gridParams, gridMethods, items} = useGrid(getList, 12);
const showModal = ref(false);
const result = ref(null);
const file = ref(null);

const failedRowsCount = computed(() => Object.keys(result.value.failed_rows).length);

function getList(pageParams) {
    let formData = new FormData();
    formData.append('file', file.value);
    formData.append('failedRows', JSON.stringify(result.value.failed_rows));
    formData.append('pageParams', JSON.stringify(pageParams));

    return inviteService.getFailedStartups(formData);
}

async function downloadFile() {
    let formData = new FormData();
    formData.append('file', file.value);
    formData.append('failedRows', JSON.stringify(result.value.failed_rows));

    const {data} = await inviteService.exportFailedRows(formData);
    download(data, 'add_startup.csv');
}

function open(data, uploadedFile) {
    result.value = data;
    file.value = uploadedFile;
    showModal.value = true;

    if (failedRowsCount.value) {
        gridMethods.resetList();
    }
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" title="Import Result" modal-class="modal-w-middle" centered>
        <template v-if="result">
            <div v-if="result.added">
                <p>{{result.added}} {{getNoun('startup', result.added)}} {{getNoun('was', result.added, 'were')}} successfully added.</p>
            </div>

            <div v-if="result.existed">
                <p>{{result.existed}} {{getNoun('startup', result.added)}} already existed on platform.</p>
            </div>

            <div v-if="failedRowsCount">
                <template v-if="failedRowsCount > 1">
                    <p>These {{failedRowsCount}} startups were unable to be added, check their data and try again:</p>
                </template>

                <template v-else>
                    <p>This startup was unable to be added, check its data and try again:</p>
                </template>
            </div>

            <template v-if="failedRowsCount">
                <div class="startups-table">
                    <div
                        v-for="(item, index) in items"
                        :key="index"
                    >
                        <div>
                            <span v-if="item.name" style="font-weight: bold">
                                {{ item.name }}
                            </span>

                            <span v-else class="status-rejected" style="font-weight: bold;">
                                No Name Added
                            </span>
                        </div>

                        <div>
                            <span v-if="item.url" :class="{'status-rejected': item.error === 'invalid_url'}">
                                <template v-if="item.error === 'invalid_url'">
                                    Not valid domain ({{item.url}})
                                </template>

                                <template v-else>
                                    {{ item.url }}
                                </template>
                            </span>

                            <span v-else class="status-rejected">
                                No Domain Added
                            </span>
                        </div>
                    </div>
                </div>

                <TablePager
                    :pages-count="gridParams.pagesCount"
                    :init-page="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                />
            </template>
        </template>

        <template #modal-footer>
            <button
                v-if="failedRowsCount"
                class="button-airy"
                @click="downloadFile"
            >
                Download CSV
            </button>

            <div v-else/>
        </template>
    </b-modal>
</template>

<style scoped>
    /*NOTE add new unique class*/
    .modal .modal-body p:not(.modal-text) {
        margin-bottom: 31px;
        font-size: 16px !important;
    }
    .startups-table {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        font-size: 14px;

        > div {
            margin-bottom: 20px;
        }
    }
</style>
